import { useState } from 'react';

/**
 * Template-variable picker. Emits `{{var}}` tokens matching the server-side
 * interpolator in packages/app/src/modules/scheduler/template-resolver.ts.
 *
 * Variables are grouped so admins authoring SMS / Email / TTS templates
 * can drop them into the body without remembering the exact spelling.
 */

export interface VariablePickerProps {
  /** Called with the token text (e.g. "{{first_name}}") to insert at the caret. */
  onInsert: (token: string) => void;
  /** Optional subset of group keys to display. Defaults to all. */
  groups?: VariableGroupKey[];
  className?: string;
}

type VariableGroupKey = 'contact' | 'event' | 'company' | 'links';

interface VariableDef {
  token: string;
  label: string;
  example?: string;
}

const GROUPS: Record<VariableGroupKey, { title: string; vars: VariableDef[] }> = {
  contact: {
    title: 'Contact',
    vars: [
      { token: '{{first_name}}', label: 'Customer first name', example: 'Sarah' },
      { token: '{{name}}', label: 'Customer full name', example: 'Sarah Mitchell' },
      { token: '{{phone}}', label: 'Customer phone' },
      { token: '{{email}}', label: 'Customer email' },
      { token: '{{contact_name}}', label: 'Contact display name' },
      { token: '{{organization}}', label: 'Contact organization' },
    ],
  },
  event: {
    title: 'Event',
    vars: [
      { token: '{{event_title}}', label: 'Event title' },
      { token: '{{event_start}}', label: 'Event start (ISO)' },
      { token: '{{event_end}}', label: 'Event end (ISO)' },
      { token: '{{start_at}}', label: 'Start date' },
      { token: '{{start_time}}', label: 'Start time' },
      { token: '{{attendee_name}}', label: 'Attendee name' },
      { token: '{{attendee_email}}', label: 'Attendee email' },
      { token: '{{attendee_phone}}', label: 'Attendee phone' },
    ],
  },
  company: {
    title: 'Company',
    vars: [
      { token: '{{co_name}}', label: 'Company name' },
      { token: '{{co_tel}}', label: 'Company phone' },
      { token: '{{co_address}}', label: 'Company address' },
      { token: '{{co_web}}', label: 'Company website' },
    ],
  },
  links: {
    title: 'Links',
    vars: [
      { token: '{{unsubscribe_url}}', label: 'Unsubscribe link' },
      { token: '{{confirm_url}}', label: 'Confirmation link' },
      { token: '{{reschedule_url}}', label: 'Reschedule link' },
    ],
  },
};

export const VARIABLE_TOKENS: string[] = Object.values(GROUPS).flatMap((g) =>
  g.vars.map((v) => v.token),
);

export function VariablePicker({
  onInsert,
  groups,
  className,
}: VariablePickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const keys = groups ?? (Object.keys(GROUPS) as VariableGroupKey[]);

  return (
    <div className={`relative inline-block ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-1.5 text-sm border border-border rounded-md bg-card hover:bg-muted"
      >
        {'{ } '}Insert variable
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-80 max-h-96 overflow-y-auto rounded-md border border-border bg-card shadow-lg p-2">
          {keys.map((k) => (
            <div key={k} className="mb-2">
              <div className="text-xs uppercase tracking-wide text-ink-muted px-2 py-1">
                {GROUPS[k].title}
              </div>
              <ul>
                {GROUPS[k].vars.map((v) => (
                  <li key={v.token}>
                    <button
                      type="button"
                      onClick={() => {
                        onInsert(v.token);
                        setOpen(false);
                      }}
                      className="w-full text-left px-2 py-1 text-sm hover:bg-muted rounded flex justify-between gap-2"
                    >
                      <span>{v.label}</span>
                      <code className="text-xs text-ink-muted">{v.token}</code>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
